// hooks/useWebSocket.ts
"use client";

import { useEffect, useRef, useState, useCallback } from "react";

interface NewReadMessage {
    type: "new_read";
    read: unknown; // or a specific type like `TireReadingRaw`
}

type MessageHandler = (data: NewReadMessage) => void;

export type WebSocketStatus = "connecting" | "connected" | "reconnecting" | "disconnected";

const MAX_RETRIES = 5;
const INITIAL_DELAY = 1000;
const MAX_DELAY = 10000;

export function useWebSocket(url: string, onMessage: MessageHandler): WebSocketStatus {
    const wsRef = useRef<WebSocket | null>(null);
    const retryCount = useRef(0);
    const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
    const pingInterval = useRef<NodeJS.Timeout | null>(null);

    const [status, setStatus] = useState<WebSocketStatus>("connecting");

    const connect = useCallback(() => {
        const attemptReconnect = () => {
            if (retryCount.current >= MAX_RETRIES) {
                console.error("[WebSocket] Max reconnection attempts reached.");
                return;
            }

            const delay = Math.min(INITIAL_DELAY * 2 ** retryCount.current, MAX_DELAY);
            retryCount.current += 1;

            console.log(`[WebSocket] Reconnecting in ${delay / 1000}s...`);
            reconnectTimeout.current = setTimeout(connect, delay);
        };

        setStatus(retryCount.current === 0 ? "connecting" : "reconnecting");

        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
            console.log("[WebSocket] Connected");
            retryCount.current = 0;
            setStatus("connected");

            ws.send("ping");
            pingInterval.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send("ping");
                }
            }, 15000);
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                onMessage(data);
            } catch (e) {
                console.error("[WebSocket] Message parse error:", e);
            }
        };

        ws.onerror = (err) => {
            console.error("[WebSocket] Error:", err);
        };

        ws.onclose = (event) => {
            console.warn("[WebSocket] Disconnected:", event.code, event.reason);
            setStatus("disconnected");

            if (pingInterval.current) {
                clearInterval(pingInterval.current);
                pingInterval.current = null;
            }

            attemptReconnect();
        };
    }, [url, onMessage]);

    useEffect(() => {
        connect();

        return () => {
            console.log("[WebSocket] Cleanup");
            wsRef.current?.close();
            if (pingInterval.current) clearInterval(pingInterval.current);
            if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
        };
    }, [url, connect]);

    return status;
}
